"use client";

import { useEffect, useState } from "react";
import { IoAddOutline, IoCloseOutline, IoStar, IoStarOutline } from "react-icons/io5";

import { isHashHref } from "@/components/nav/NavAnchor";
import {
  BOOKMARKS_EMPTY,
  BOOKMARKS_KEY,
  BOOKMARKS_SUBTITLE,
  BOOKMARKS_TITLE,
} from "@/components/nav/useNavBookmarks";
import {
  NAV_CORAL_MARK,
  NAV_NOTE,
  ROW_ACTION_ROOM,
  panelRowClass,
  rowActionClass,
  sheetRowClass,
} from "@/components/nav/nav-styles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NAV_BOOKMARKS_FULL_MESSAGE } from "@/lib/navigation/nav-bookmarks";
import { cn } from "@/lib/utils";

/**
 * The bookmark controls in the side nav (SAL-3283), all driven by
 * `useNavBookmarks`:
 *   • the star at the end of a menu link, which saves or removes that link;
 *   • the remove button at the end of a bookmark;
 *   • "Bookmark this page", above the list while the page isn't saved;
 *   • saved pages the viewer's menus no longer show, which can only be removed;
 *   • the Bookmarks view the panel shows and the section the drawer lists.
 * The rail's Bookmarks button is in NavRail beside the other menus.
 */

function RowAction({ label, tooltip = label, on, pressed, disabled, onClick, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={pressed}
          aria-disabled={disabled || undefined}
          onClick={disabled ? undefined : onClick}
          className={rowActionClass(on)}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-56">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

/** A menu link's star. In-page anchors and off-site links get none. */
export function BookmarkStar({ link, bookmarks }) {
  if (!link.href || link.external || isHashHref(link.href)) return null;
  const on = bookmarks.isSaved(link.href);
  const Icon = on ? IoStar : IoStarOutline;
  return (
    <RowAction
      label={on ? `Remove ${link.label} from bookmarks` : `Bookmark ${link.label}`}
      tooltip={
        on
          ? "Remove from bookmarks"
          : bookmarks.full
            ? NAV_BOOKMARKS_FULL_MESSAGE
            : "Bookmark"
      }
      on={on}
      pressed={on}
      disabled={!on && bookmarks.full}
      onClick={() => bookmarks.toggle(link)}
    >
      <Icon className="h-4 w-4" />
    </RowAction>
  );
}

function BookmarkRemove({ link, bookmarks }) {
  return (
    <RowAction
      label={`Remove ${link.label} from bookmarks`}
      tooltip="Remove from bookmarks"
      onClick={() => bookmarks.remove(link.href)}
    >
      <IoCloseOutline className="h-4 w-4" />
    </RowAction>
  );
}

// The page's own heading, the name the person just read. Read once this
// control is on screen, by which time the page has rendered.
function usePageHeading(href) {
  const [heading, setHeading] = useState("");
  useEffect(() => {
    const text = document.querySelector("main h1")?.textContent || "";
    setHeading(text.replace(/\s+/g, " ").trim());
  }, [href]);
  return heading;
}

/**
 * "Bookmark this page" with the name it will be saved under beneath it, or
 * why it can't be, while the page isn't saved.
 */
function AddCurrentPage({ bookmarks, rowClass }) {
  const page = bookmarks.currentPage;
  const heading = usePageHeading(page?.href);
  if (!page) return null;
  if (bookmarks.full) {
    return <p className={NAV_NOTE}>{NAV_BOOKMARKS_FULL_MESSAGE}</p>;
  }
  const name = page.label || heading || page.fallbackLabel;
  return (
    <button
      type="button"
      onClick={() => bookmarks.addCurrentPage(name)}
      className={cn(rowClass, "w-full text-left")}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        <IoAddOutline className={cn("h-[18px] w-[18px]", NAV_CORAL_MARK)} />
      </span>
      <span className="min-w-0">
        <span className="block">Bookmark this page</span>
        <span className="block truncate text-xs text-gray-500">{name}</span>
      </span>
    </button>
  );
}

/**
 * Saved pages this viewer's menus no longer show (a role change, a plugin
 * switched off). Not links, since the page won't open for them, but they
 * still count toward the limit, so each keeps its remove button.
 */
function UnavailableBookmarks({ bookmarks, rowClass }) {
  return bookmarks.unavailable.map((bookmark) => (
    <div key={bookmark.href} className="group/row relative">
      <div
        className={cn(
          rowClass,
          ROW_ACTION_ROOM,
          "text-gray-400 hover:bg-transparent hover:text-gray-400",
        )}
      >
        <span className="min-w-0">
          <span className="block truncate">{bookmark.label}</span>
          <span className="block text-xs">Not in your menus</span>
        </span>
      </div>
      <BookmarkRemove link={bookmark} bookmarks={bookmarks} />
    </div>
  ));
}

function BookmarksLead({ bookmarks, rowClass }) {
  return (
    <>
      <AddCurrentPage bookmarks={bookmarks} rowClass={rowClass} />
      <UnavailableBookmarks bookmarks={bookmarks} rowClass={rowClass} />
    </>
  );
}

function removeAction(bookmarks) {
  return function removeButton(link) {
    return <BookmarkRemove link={link} bookmarks={bookmarks} />;
  };
}

/** What the panel shows when the rail's Bookmarks button is pressed. */
export function bookmarksPanelView(bookmarks) {
  return {
    key: BOOKMARKS_KEY,
    title: BOOKMARKS_TITLE,
    subtitle: BOOKMARKS_SUBTITLE,
    items: bookmarks.items,
    lead: <BookmarksLead bookmarks={bookmarks} rowClass={panelRowClass(false)} />,
    empty: bookmarks.unavailable.length === 0 && BOOKMARKS_EMPTY,
    rowAction: removeAction(bookmarks),
  };
}

/** The same list as the drawer's first section on a phone. */
export function bookmarksDrawerSection(bookmarks) {
  return {
    label: BOOKMARKS_TITLE,
    subtitle: BOOKMARKS_SUBTITLE,
    items: bookmarks.items,
    lead: <BookmarksLead bookmarks={bookmarks} rowClass={sheetRowClass(false)} />,
    empty: bookmarks.unavailable.length === 0 && BOOKMARKS_EMPTY,
    rowAction: removeAction(bookmarks),
    // The route's own menu opens with the drawer, even when the page is
    // also bookmarked.
    autoOpen: false,
  };
}
